const NON_EDITABLE_ATTRIBUTES = [
  'data-case-detail',
  'data-template',
  'data-case-detail-table',
] as const

const NON_EDITABLE_SELECTOR = NON_EDITABLE_ATTRIBUTES.map(
  (attr) => `[${attr}]`,
).join(', ')

const isNonEditableElement = (element: Element | null): boolean => {
  if (!element) return false
  return NON_EDITABLE_ATTRIBUTES.some((attr) => element.hasAttribute(attr))
}

const createStyledSpan = (
  editor: Editor,
  styles: string,
): HTMLElement | null => {
  return editor?.dom?.create?.(
    'span',
    {
      style: styles,
    },
    '\u200B',
  ) // Zero-width space
}

const moveCursorIntoStyledSpan = (
  editor: Editor,
  styledSpan: Element,
): void => {
  const br = styledSpan?.querySelector?.('br')
  if (br) {
    editor?.selection?.setCursorLocation?.(styledSpan, 0)
  } else {
    let targetNode: Node = styledSpan
    let offset = 0

    if (styledSpan.lastChild) {
      if (styledSpan.lastChild.nodeType === Node.TEXT_NODE) {
        targetNode = styledSpan.lastChild
        offset = (styledSpan.lastChild as Text).length
      } else {
        targetNode = styledSpan
        offset = styledSpan.childNodes.length
      }
    }

    editor?.selection?.setCursorLocation?.(targetNode, offset)
  }
}

const findNonEditableElementNearCursor = (
  editor: Editor,
): Element | null | undefined => {
  const selection = editor?.selection
  const range = selection?.getRng?.()

  if (!range) return null

  // Check the node immediately before the cursor (most likely the just-inserted element)
  const nodeBefore = range?.startContainer?.previousSibling
  const isElementNode = nodeBefore?.nodeType === 1

  if (isElementNode) {
    const elementBefore = nodeBefore as Element
    // Check if this element or its last child is non-editable
    const isNonEditable = elementBefore?.matches?.(NON_EDITABLE_SELECTOR)
    const lastChildNonEditable = elementBefore?.lastElementChild?.matches?.(
      NON_EDITABLE_SELECTOR,
    )

    if (isNonEditable) {
      return elementBefore
    } else if (lastChildNonEditable) {
      return elementBefore.lastElementChild
    }
  }

  // Fallback: if still not found, check if parent of startContainer is non-editable
  const containerParent = range?.startContainer?.parentElement
  return containerParent?.closest?.(NON_EDITABLE_SELECTOR) || null
}

const findOutermostNonEditableElement = (
  element: Element,
): Element | null | undefined => {
  let outermost = element
  let parent = element?.parentElement

  while (parent) {
    const parentNonEditable = parent?.closest?.(NON_EDITABLE_SELECTOR)
    if (parentNonEditable) {
      outermost = parentNonEditable
      parent = parentNonEditable?.parentElement
    } else {
      break
    }
  }

  return outermost
}

const handleBlockElement = (editor: Editor, blockElement: Element): boolean => {
  if (blockElement?.tagName !== 'DIV') return false

  const parent = blockElement?.parentElement
  if (parent) {
    const siblings = Array.from(parent.childNodes)
    const elementIndex = siblings.indexOf(blockElement)
    editor?.selection?.setCursorLocation?.(parent, elementIndex + 1)
  }
  return true
}

const handleInlineElement = (editor: Editor, inlineElement: Element): void => {
  const elementStyles = inlineElement?.getAttribute?.('style') || ''
  const nextSibling = inlineElement?.nextSibling

  // Check if there's already a styled span we can use (that's not non-editable)
  const nextElement =
    nextSibling?.nodeType === 1 ? (nextSibling as Element) : null
  const isStyledSpan =
    nextElement?.tagName === 'SPAN' &&
    nextElement?.hasAttribute?.('style') &&
    !isNonEditableElement(nextElement)

  if (isStyledSpan && nextElement) {
    // Position cursor AFTER the existing styled span to prevent nesting
    const parent = nextElement?.parentElement
    if (parent) {
      const siblings = Array.from(parent.childNodes)
      const spanIndex = siblings.indexOf(nextElement)
      editor?.selection?.setCursorLocation?.(parent, spanIndex + 1)
    }
  } else {
    // Create a styled span to preserve formatting context
    const styleSpan = createStyledSpan(editor, elementStyles)

    if (styleSpan && inlineElement) {
      editor?.dom?.insertAfter?.(styleSpan, inlineElement)

      // Position cursor AFTER the styled span (as sibling), not inside it
      // This prevents nested spans when inserting multiple case details
      const parent = styleSpan?.parentElement
      if (parent) {
        const siblings = Array.from(parent.childNodes)
        const spanIndex = siblings.indexOf(styleSpan)
        editor?.selection?.setCursorLocation?.(parent, spanIndex + 1)
      }
    }
  }
}

const findEditableStyledSpan = (currentNode: Node): Element | null => {
  const currentElement = currentNode as Element
  const parentParagraph = currentElement?.closest?.('p')
  const allStyledSpans = parentParagraph?.querySelectorAll?.('span[style]')

  const found = Array.from(allStyledSpans || []).find(
    (span) => !isNonEditableElement(span),
  )
  return found || null
}

const handleStyleInheritance = (editor: Editor, currentNode: Node): void => {
  const currentElement = currentNode as Element
  const parentParagraph = currentElement?.closest?.('p')

  // Check if there are any non-editable elements with styles we can inherit from
  const anyNonEditableSpan = parentParagraph?.querySelector?.(
    'span[data-case-detail][style], span[data-template][style], span[data-case-detail-table][style]',
  )

  if (anyNonEditableSpan) {
    const inheritedStyles = anyNonEditableSpan?.getAttribute?.('style') || ''

    // Create a styled span to maintain formatting context
    const styleSpan = createStyledSpan(editor, inheritedStyles)

    if (styleSpan) {
      // Only insert if cursor is collapsed (no text selected) to avoid replacing content
      const isCollapsed = editor?.selection?.isCollapsed?.()
      if (isCollapsed) {
        const range = editor?.selection?.getRng?.()
        if (range) {
          range.insertNode(styleSpan)
          editor?.selection?.setCursorLocation?.(styleSpan, 1)
        }
      }
    }
  }
}

export const ensureProperCursorPosition = (editor: Editor) => {
  const currentNode = editor?.selection?.getNode?.()
  if (!currentNode) return

  const currentElement = currentNode as Element

  // Step 1: Find non-editable element (either cursor is inside it, or it was just inserted)
  const nonEditableElement =
    currentElement?.closest?.(NON_EDITABLE_SELECTOR) ||
    findNonEditableElementNearCursor(editor)

  // Step 2: If we found a non-editable element, position cursor after it
  if (nonEditableElement) {
    const outermostElement = findOutermostNonEditableElement(nonEditableElement)
    if (!outermostElement) return

    // Handle block-level elements (divs)
    const wasBlockElement = handleBlockElement(editor, outermostElement)
    if (wasBlockElement) return

    // Handle inline elements (spans) - create/reuse styled span for formatting
    handleInlineElement(editor, outermostElement)
    return
  }

  // Step 3: No non-editable element found - ensure cursor is in styled context
  const editableStyledSpan = findEditableStyledSpan(currentNode)
  const isInsideStyledSpan = currentElement?.closest?.(
    'span[style]:not([data-case-detail]):not([data-template]):not([data-case-detail-table])',
  )

  if (!isInsideStyledSpan && editableStyledSpan) {
    // Move cursor into existing styled span
    moveCursorIntoStyledSpan(editor, editableStyledSpan)
  } else if (!isInsideStyledSpan && !editableStyledSpan) {
    // Create new styled span with inherited styles from nearby non-editable elements
    handleStyleInheritance(editor, currentNode)
  }
}
